import { IconButton, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useNavigate, useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { CIVIL_DRAW_ROUTE, type Map as CivilDrawMap, type Project } from '~/common/types';
import { Dropdown } from '~/components2/Dropdown';
import { navSectionHeaderClassName } from '../../common/LeftNav/LeftNavSection';
import type { ProjectDetailsParams } from '..';
import DeleteMapDialog from './common/DeleteMapDialog';
import store from './store';

interface MapItemProps {
  map: CivilDrawMap;
  project: Project;
}

const MapItem = ({ map, project }: MapItemProps) => {
  const { projectId, currentMapId } = useSnapshot(store) as typeof store;
  const [name, setName] = useState(map.name);
  const navigate = useNavigate();

  const isActive = currentMapId === map.id;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (name !== map.name) {
      store.updateMapName(map.id, name);
    }
  }, [name]);

  const setCurrentMap = (id: string) => {
    store.resetCurrentObjectId();
    store.setCurrentMapId(id);
    navigate({ to: '/projects/$projectId/$tabId/$mapId', params: { projectId, tabId: CIVIL_DRAW_ROUTE, mapId: id } });
  };

  return (
    <div
      key={`${map.id}`}
      className={classNames(
        'group flex grow cursor-pointer items-center justify-between rounded-[6px] px-2 py-1 transition-all outline-none',
        'font-noto text-sm font-normal text-grey-90',
        'hover:bg-grey-20',
        isActive && 'bg-blue-10 text-blue-60 font-medium',
      )}
      onClick={() => setCurrentMap(map.id)}
    >
      <span>{name}</span>
      {isActive && !project.sandboxProject && (
        <Dropdown
          side="right"
          alignOffset={20}
          triggerElement={
            <IconButton
              variant="hollow"
              size="icon"
              icon="dotsVertical"
              iconSize={14}
              className="h-[24px] min-w-[24px] hover:bg-grey-30"
              onClick={(e) => e.stopPropagation()}
              aria-label={name ? `More actions for ${name}` : 'More actions'}
              data-testid={`civildraw-map-options-${map.id}`}
            />
          }
          items={[
            {
              type: 'simple',
              label: 'Rename Map',
              isAllowedTo: Action.EditCivilDraw,
              onClick: async () => {
                const newName = window.prompt('Enter new map name:', name);
                if (!newName) return;
                setName(newName);
              },
            },
            {
              type: 'dialog',
              label: 'Delete Map',
              isAllowedTo: Action.DeleteCivilDraw,
              dialogTitle: 'Delete Map?',
              dialogContent: (closeDialog) => <DeleteMapDialog map={map} closeDialog={closeDialog} />,
            },
          ]}
        />
      )}
    </div>
  );
};

export default function MapList() {
  const { project, currentMap } = useSnapshot(store) as typeof store;
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      await store.loadProject(projectId);
    })();
  }, [projectId]);

  const setCurrentMap = (id: string) => {
    store.resetCurrentObjectId();
    store.setCurrentMapId(id);
    navigate({ to: '/projects/$projectId/$tabId/$mapId', params: { projectId, tabId: CIVIL_DRAW_ROUTE, mapId: id } });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const maps = useMemo(() => {
    if (!project.isLoading) {
      const _maps = Object.values(project.maps)
        .sort((a, b) => b.updatedAt.valueOf() - a.updatedAt.valueOf())
        .map((m) => <MapItem key={m.id} map={m} project={project} />);

      return _maps;
    } else {
      return <SkeletonSpinnerCentered />;
    }
  }, [project, currentMap]);

  const createNewMap = async () => {
    const name = window.prompt('Enter new map name:');

    if (name) {
      const newMapId = await store.newMap(name);

      setCurrentMap(newMapId);
      if (project.isLoading === false) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawMapCreated, {
          project_id: project.id,
          organization_id: project.organization || undefined,
          map_id: newMapId,
        });
      }
    }
  };

  return (
    <div id="left-nav-civildraw" className="flex flex-col">
      <div className={navSectionHeaderClassName}>CivilDraw</div>
      <Can action={Action.ViewCivilDraw}>
        <div className="mb-2">{maps}</div>
      </Can>

      <Can action={Action.CreateCivilDraw}>
        <IconButton
          id="new-civildraw-map"
          icon="plus"
          variant="secondary"
          onClick={createNewMap}
          data-testid="civildraw-new-map-button"
        >
          New Map
        </IconButton>
      </Can>
    </div>
  );
}
